import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useState, useRef, useEffect } from "react";
import { Leaf, User, LogOut, ChevronDown, Settings, Shield, Layers, Palette, Receipt, DollarSign, BookOpen } from "lucide-react";

export default function Navbar({ saving }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <nav className="bg-white border-b border-[#E2E8E3] sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <Leaf className="w-5 h-5 text-sage-500 group-hover:text-sage-600 transition-colors" />
          <span className="font-heading text-lg font-semibold text-sage-800 tracking-tight">TheKaizenCabinet</span>
          <span className="text-sage-400 text-xs font-body hidden sm:block">Your Lean Toolbox</span>
        </Link>

        <div className="flex items-center gap-4">
          {saving && (
            <div className="flex items-center gap-1.5 text-xs text-sage-400">
              <div className="w-3 h-3 border border-sage-400 border-t-transparent rounded-full animate-spin" />
              <span>Saving...</span>
            </div>
          )}
          <div className="relative" ref={ref}>
            <button
              data-testid="user-menu-button"
              onClick={() => setOpen(!open)}
              className="flex items-center gap-2 text-sm text-sage-700 hover:text-sage-900 transition-colors"
            >
              <div className="w-7 h-7 bg-sage-100 rounded-full flex items-center justify-center">
                <span className="text-sage-600 text-xs font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </div>
              <span className="hidden sm:block font-medium">{user?.name}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-[#E2E8E3] rounded-md shadow-lg w-44 z-50 animate-fade-in">
                <div className="px-3 py-2 border-b border-sage-50">
                  <p className="text-xs font-semibold text-sage-700 truncate">{user?.email}</p>
                  <p className="text-xs text-sage-400 capitalize">{user?.role}</p>
                </div>
                <Link
                  to="/profile"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-sage-700 hover:bg-sage-50 transition-colors"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Profile & Settings
                </Link>
                <Link to="/catalogue" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-sage-700 hover:bg-sage-50 transition-colors">
                  <Layers className="w-3.5 h-3.5" /> Tools Catalogue
                </Link>
                <Link
                  to="/docs"
                  data-testid="docs-nav-link"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-sage-700 hover:bg-sage-50 transition-colors"
                >
                  <BookOpen className="w-3.5 h-3.5" /> Tool Docs
                </Link>
                <Link to="/pricing" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-sage-700 hover:bg-sage-50 transition-colors">
                  <DollarSign className="w-3.5 h-3.5" /> Plans & Pricing
                </Link>
                <Link to="/billing" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-sage-700 hover:bg-sage-50 transition-colors">
                  <Receipt className="w-3.5 h-3.5" /> Billing
                </Link>
                <Link to="/branding" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-sage-700 hover:bg-sage-50 transition-colors">
                  <Palette className="w-3.5 h-3.5" /> Whitelabel
                </Link>
                {user?.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="admin-panel-link"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-sage-700 hover:bg-sage-50 transition-colors"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    Admin Panel
                  </Link>
                )}
                {user?.role === "superadmin" && (
                  <Link
                    to="/superadmin"
                    data-testid="superadmin-panel-link"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-sage-700 hover:bg-sage-50 transition-colors"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    Super Admin
                  </Link>
                )}
                <button
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
